import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Users, Calendar, Mail, Phone, Clock, ChevronDown, ChevronUp, Search, FileText, Plus, Trash2, Eye, Inbox } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem('admin_token', res.data.token);
      onLogin(res.data.token);
      toast.success('Logged in successfully');
    } catch {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#003366] to-[#001a33] px-4">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardHeader className="text-center pb-2">
          <img
            src="/assets/logo.png"
            alt="Aquarist Pools Logo"
            className="h-16 w-auto mx-auto mb-4"
          />
          <CardTitle className="text-2xl font-bold text-[#003366]">Admin Dashboard</CardTitle>
          <p className="text-sm text-gray-500 mt-1">Aquarist Pools & Fitness</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
              <Input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@aquaristpools.com"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Password</label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>
            <Button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#003366] hover:bg-[#002244] text-white py-5 text-base font-semibold"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [data, setData] = useState({ inquiries: [], bookings: [], inquiries_count: 0, bookings_count: 0 });
  const [blogPosts, setBlogPosts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');
  const [search, setSearch] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [blogForm, setBlogForm] = useState({ title: '', excerpt: '', content: '', cover_image: '', category: 'General', tags: '', featured: false, images: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    fetchBlogPosts();
    fetchSubscribers();
  }, [token]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        onLogout();
      }
    }
  };

  const fetchBlogPosts = async () => {
    try {
      const res = await axios.get(`${API}/admin/blog-posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogPosts(res.data.posts || []);
    } catch {}
  };

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletter`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(res.data.subscribers || []);
    } catch {}
  };

  const handleDeleteSubscriber = async (email) => {
    if (!window.confirm(`Remove ${email} from the newsletter?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletter/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscriber removed');
      fetchSubscribers();
    } catch {
      toast.error('Failed to remove subscriber');
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...blogForm,
        tags: blogForm.tags.split(',').map(t => t.trim()).filter(Boolean),
        images: blogForm.images.split('\n').map(u => u.trim()).filter(Boolean)
      };
      await axios.post(`${API}/admin/blog`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blog post created');
      setShowBlogForm(false);
      setBlogForm({ title: '', excerpt: '', content: '', cover_image: '', category: 'General', tags: '', featured: false, images: '' });
      fetchBlogPosts();
    } catch {
      toast.error('Failed to create post');
    }
  };

  const handleDeletePost = async (slug) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API}/admin/blog/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      fetchBlogPosts();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    onLogout();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const filteredBookings = data.bookings.filter(b =>
    [b.name, b.email, b.phone, b.service].some(f => f?.toLowerCase().includes(search.toLowerCase()))
  );

  const filteredInquiries = data.inquiries.filter(i =>
    [i.name, i.email, i.phone, i.message].some(f => f?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/assets/logo.png"
              alt="Logo"
              className="h-10 w-auto"
            />
            <div>
              <h1 className="text-lg font-bold">Admin Dashboard</h1>
              <p className="text-xs text-[#00AEEF]">Aquarist Pools & Fitness</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="admin-back-to-site"
              onClick={() => navigate('/')}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 text-sm"
            >
              View Site
            </Button>
            <Button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 text-sm"
            >
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card
            data-testid="stat-bookings"
            className={`cursor-pointer border-2 transition-all duration-200 ${activeTab === 'bookings' ? 'border-[#00AEEF] shadow-lg' : 'border-transparent hover:border-gray-200'}`}
            onClick={() => setActiveTab('bookings')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-[#00AEEF]/10 flex items-center justify-center">
                <Calendar className="w-7 h-7 text-[#00AEEF]" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Quote Requests</p>
                <p className="text-3xl font-bold text-[#003366]">{data.bookings_count}</p>
              </div>
            </CardContent>
          </Card>
          <Card
            data-testid="stat-inquiries"
            className={`cursor-pointer border-2 transition-all duration-200 ${activeTab === 'inquiries' ? 'border-[#00AEEF] shadow-lg' : 'border-transparent hover:border-gray-200'}`}
            onClick={() => setActiveTab('inquiries')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center">
                <Users className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Contact Inquiries</p>
                <p className="text-3xl font-bold text-[#003366]">{data.inquiries_count}</p>
              </div>
            </CardContent>
          </Card>
          <Card
            data-testid="stat-blog"
            className={`cursor-pointer border-2 transition-all duration-200 ${activeTab === 'blog' ? 'border-[#00AEEF] shadow-lg' : 'border-transparent hover:border-gray-200'}`}
            onClick={() => setActiveTab('blog')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">
                <FileText className="w-7 h-7 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Blog Posts</p>
                <p className="text-3xl font-bold text-[#003366]">{blogPosts.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card
            data-testid="stat-newsletter"
            className={`cursor-pointer border-2 transition-all duration-200 ${activeTab === 'newsletter' ? 'border-[#00AEEF] shadow-lg' : 'border-transparent hover:border-gray-200'}`}
            onClick={() => setActiveTab('newsletter')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center">
                <Inbox className="w-7 h-7 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Subscribers</p>
                <p className="text-3xl font-bold text-[#003366]">{subscribers.length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="admin-search-input"
            placeholder={`Search ${activeTab === 'bookings' ? 'quote requests' : 'contact inquiries'}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Bookings Table */}
        {activeTab === 'bookings' && (
          <Card data-testid="bookings-table-card" className="shadow-lg border-0">
            <CardHeader className="border-b bg-gray-50/50">
              <CardTitle className="text-lg text-[#003366]">Quote Requests ({filteredBookings.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {filteredBookings.length === 0 ? (
                <p className="p-8 text-center text-gray-400">No quote requests found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">Name</TableHead>
                        <TableHead className="font-semibold">Contact</TableHead>
                        <TableHead className="font-semibold">Service</TableHead>
                        <TableHead className="font-semibold">Project Type</TableHead>
                        <TableHead className="font-semibold">Budget</TableHead>
                        <TableHead className="font-semibold">Date</TableHead>
                        <TableHead className="font-semibold w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBookings.map((b, i) => (
                        <React.Fragment key={b.id || i}>
                          <TableRow
                            className="hover:bg-[#00AEEF]/5 cursor-pointer transition-colors"
                            onClick={() => setExpandedRow(expandedRow === `b-${i}` ? null : `b-${i}`)}
                          >
                            <TableCell className="font-medium text-[#003366]">{b.name}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-xs flex items-center gap-1"><Mail className="w-3 h-3" />{b.email}</span>
                                <span className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" />{b.phone}</span>
                              </div>
                            </TableCell>
                            <TableCell><span className="bg-[#00AEEF]/10 text-[#00AEEF] px-2 py-1 rounded-full text-xs font-medium">{b.service}</span></TableCell>
                            <TableCell className="text-sm">{b.projectType}</TableCell>
                            <TableCell className="text-sm">{b.budget || '—'}</TableCell>
                            <TableCell className="text-xs text-gray-500"><Clock className="w-3 h-3 inline mr-1" />{formatDate(b.created_at)}</TableCell>
                            <TableCell>{expandedRow === `b-${i}` ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</TableCell>
                          </TableRow>
                          {expandedRow === `b-${i}` && (
                            <TableRow>
                              <TableCell colSpan={7} className="bg-blue-50/50 p-4">
                                <p className="text-sm font-medium text-gray-600 mb-1">Message:</p>
                                <p className="text-sm text-gray-800">{b.message}</p>
                                {b.preferredDate && <p className="text-sm text-gray-500 mt-2">Preferred Date: {b.preferredDate}</p>}
                                <p className="text-xs text-gray-400 mt-2">Status: <span className="font-medium text-yellow-600">{b.status}</span></p>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Contact Inquiries Table */}
        {activeTab === 'inquiries' && (
          <Card data-testid="inquiries-table-card" className="shadow-lg border-0">
            <CardHeader className="border-b bg-gray-50/50">
              <CardTitle className="text-lg text-[#003366]">Contact Inquiries ({filteredInquiries.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {filteredInquiries.length === 0 ? (
                <p className="p-8 text-center text-gray-400">No contact inquiries found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">Name</TableHead>
                        <TableHead className="font-semibold">Email</TableHead>
                        <TableHead className="font-semibold">Phone</TableHead>
                        <TableHead className="font-semibold">Message</TableHead>
                        <TableHead className="font-semibold">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInquiries.map((inq, i) => (
                        <TableRow key={inq.id || i} className="hover:bg-green-50/50 transition-colors">
                          <TableCell className="font-medium text-[#003366]">{inq.name}</TableCell>
                          <TableCell className="text-sm">{inq.email}</TableCell>
                          <TableCell className="text-sm">{inq.phone}</TableCell>
                          <TableCell className="text-sm max-w-xs truncate">{inq.message}</TableCell>
                          <TableCell className="text-xs text-gray-500"><Clock className="w-3 h-3 inline mr-1" />{formatDate(inq.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Blog Management */}
        {activeTab === 'blog' && (
          <Card data-testid="blog-management-card" className="shadow-lg border-0">
            <CardHeader className="border-b bg-gray-50/50 flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-[#003366]">Blog Posts ({blogPosts.length})</CardTitle>
              <Button
                data-testid="admin-create-blog-btn"
                onClick={() => setShowBlogForm(!showBlogForm)}
                className="bg-[#00AEEF] hover:bg-[#0098d4] text-white text-sm"
              >
                <Plus className="w-4 h-4 mr-1" /> New Post
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {showBlogForm && (
                <form onSubmit={handleCreatePost} className="p-6 border-b bg-blue-50/30 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Title *</label>
                      <Input value={blogForm.title} onChange={e => setBlogForm({...blogForm, title: e.target.value})} required placeholder="Blog post title" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Category</label>
                      <Input value={blogForm.category} onChange={e => setBlogForm({...blogForm, category: e.target.value})} placeholder="e.g. Projects, Guide, Technology" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Excerpt *</label>
                    <Input value={blogForm.excerpt} onChange={e => setBlogForm({...blogForm, excerpt: e.target.value})} required placeholder="Short summary" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Cover Image URL *</label>
                    <Input value={blogForm.cover_image} onChange={e => setBlogForm({...blogForm, cover_image: e.target.value})} required placeholder="https://..." />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Content (HTML) *</label>
                    <textarea
                      value={blogForm.content}
                      onChange={e => setBlogForm({...blogForm, content: e.target.value})}
                      required
                      rows={6}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      placeholder="<h2>Section</h2><p>Content...</p>"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Tags (comma separated)</label>
                      <Input value={blogForm.tags} onChange={e => setBlogForm({...blogForm, tags: e.target.value})} placeholder="pool, construction, tips" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Gallery Image URLs (one per line)</label>
                      <textarea
                        value={blogForm.images}
                        onChange={e => setBlogForm({...blogForm, images: e.target.value})}
                        rows={2}
                        className="w-full border rounded-md px-3 py-2 text-sm"
                        placeholder="https://image1.jpg&#10;https://image2.jpg"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={blogForm.featured} onChange={e => setBlogForm({...blogForm, featured: e.target.checked})} />
                      Featured Post
                    </label>
                    <Button type="submit" className="bg-[#003366] hover:bg-[#002244] text-white">Create Post</Button>
                    <Button type="button" variant="outline" onClick={() => setShowBlogForm(false)}>Cancel</Button>
                  </div>
                </form>
              )}
              {blogPosts.length === 0 ? (
                <p className="p-8 text-center text-gray-400">No blog posts yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">Title</TableHead>
                        <TableHead className="font-semibold">Category</TableHead>
                        <TableHead className="font-semibold">Featured</TableHead>
                        <TableHead className="font-semibold">Date</TableHead>
                        <TableHead className="font-semibold w-24">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {blogPosts.map((p, i) => (
                        <TableRow key={p.id || i} className="hover:bg-purple-50/50 transition-colors">
                          <TableCell className="font-medium text-[#003366] max-w-xs truncate">{p.title}</TableCell>
                          <TableCell><span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium">{p.category}</span></TableCell>
                          <TableCell>{p.featured ? <span className="text-[#00AEEF] font-semibold text-xs">Yes</span> : <span className="text-gray-400 text-xs">No</span>}</TableCell>
                          <TableCell className="text-xs text-gray-500"><Clock className="w-3 h-3 inline mr-1" />{formatDate(p.created_at)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <button onClick={() => window.open(`/blog/${p.slug}`, '_blank')} className="text-gray-400 hover:text-[#00AEEF]"><Eye className="w-4 h-4" /></button>
                              <button onClick={() => handleDeletePost(p.slug)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Newsletter Subscribers Table */}
        {activeTab === 'newsletter' && (
          <Card data-testid="newsletter-table-card" className="shadow-lg border-0">
            <CardHeader className="border-b bg-gray-50/50 flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-[#003366]">Newsletter Subscribers ({subscribers.length})</CardTitle>
              {subscribers.length > 0 && (
                <a
                  href={`data:text/csv;charset=utf-8,${encodeURIComponent('email,source,subscribed_at\n' + subscribers.map(s => `${s.email},${s.source},${s.subscribed_at}`).join('\n'))}`}
                  download="newsletter-subscribers.csv"
                  className="text-sm text-[#00AEEF] hover:underline font-semibold"
                  data-testid="newsletter-export-csv"
                >
                  Export CSV
                </a>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {subscribers.length === 0 ? (
                <p className="p-8 text-center text-gray-400">No subscribers yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">Email</TableHead>
                        <TableHead className="font-semibold">Source</TableHead>
                        <TableHead className="font-semibold">Subscribed At</TableHead>
                        <TableHead className="font-semibold w-16">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscribers.map((s, i) => (
                        <TableRow key={s.email || i} className="hover:bg-amber-50/40 transition-colors">
                          <TableCell className="font-medium text-[#003366]">{s.email}</TableCell>
                          <TableCell><span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">{s.source}</span></TableCell>
                          <TableCell className="text-xs text-gray-500"><Clock className="w-3 h-3 inline mr-1" />{formatDate(s.subscribed_at)}</TableCell>
                          <TableCell>
                            <button onClick={() => handleDeleteSubscriber(s.email)} className="text-gray-400 hover:text-red-500" data-testid={`newsletter-delete-${s.email}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  if (!token) return <AdminLogin onLogin={setToken} />;
  return <Dashboard token={token} onLogout={() => setToken(null)} />;
};

export default Admin;
